import { useMemo, type ReactElement } from 'react'
import { Button } from '@ascleo/ui/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogClose
} from '@ascleo/ui/components/ui/dialog'
import { Separator } from '@ascleo/ui/components/ui/separator'
import { cn } from '@ascleo/ui/lib/utils'
import { Check, ChevronRight } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useFlags } from '@ascleo/icons/hooks'
import { FRONTDESK_LANGUAGE_CATALOG } from '../lib/i18n/language-catalog'

interface LanguageSelectorProps {
  /** Current session language. Falls back to i18next's active language when omitted. */
  value?: string
  /** Called when the user chooses a language. Falls back to i18next when omitted. */
  onLanguageChange?: (language: string) => void
}

/** Languages whose country flag would misname their speakers. */
const LANGUAGES_WITHOUT_FLAG = new Set(['ar', 'fa', 'ps', 'sr'])

/**
 * Resolve the flag icon shown beside a language, or none where a country flag
 * would misname the language's speakers.
 *
 * @since 1.1.8
 * @example
 * ```tsx
 * const flagFor = useLanguageFlag()
 * const Flag = flagFor('tr')
 * ```
 */
export function useLanguageFlag() {
  const { getFlagByLanguage } = useFlags()
  return (code: string) => (LANGUAGES_WITHOUT_FLAG.has(code) ? undefined : getFlagByLanguage(code))
}

/**
 * Kiosk language picker for the current self-service session.
 *
 * @since 1.0.0
 * @example
 * ```tsx
 * <LanguageSelector value={sessionLanguage} onLanguageChange={setSessionLanguage} />
 * ```
 */
export function LanguageSelector({
  value,
  onLanguageChange,
}: LanguageSelectorProps = {}): ReactElement {
  const { t, i18n } = useTranslation()
  // A flag names a country, not a language. For contested entries the language
  // name stands alone rather than under someone else's flag.
  const flagForLanguage = useLanguageFlag()
  const selectedCode = (value ?? i18n.resolvedLanguage ?? i18n.language ?? 'de').split('-')[0] ?? 'de'

  const selected = useMemo(
    () => FRONTDESK_LANGUAGE_CATALOG.find((l) => l.code === selectedCode) || FRONTDESK_LANGUAGE_CATALOG[0],
    [selectedCode]
  )

  const SelectedFlag = flagForLanguage(selected.code)

  return (
    <Dialog>
      <DialogTrigger
        render={<Button data-testid="language-selector-button" variant="outline" size="lg" aria-label={t('self-service.language-selector.title')} />}
      >
        {SelectedFlag && <SelectedFlag className="size-6 rounded-sm" />}
        <span className="ml-2 hidden @4xl/kiosk-header:inline">{selected.label}</span>
      </DialogTrigger>

      <DialogContent data-testid="frontdesk-language-selector-dialog" className="max-w-2xl h-[calc(100vh-10rem)] flex flex-col overflow-hidden">
        <DialogHeader
          className={cn(
            'absolute top-0 left-0 right-0 p-6 bg-transparent backdrop-blur-lg',
            'z-20'
          )}
        >
          <DialogTitle>{t('self-service.language-selector.title')}</DialogTitle>
          <DialogDescription>{t('self-service.language-selector.description')}</DialogDescription>
        </DialogHeader>

        <div
          className="overflow-y-auto pt-[88px] pb-[88px]"
          onScroll={(e) => {
            const target = e.currentTarget
            const header = target.previousElementSibling as HTMLElement
            if (header) {
              const isScrolled = target.scrollTop > 0
              header.classList.toggle('border-b', isScrolled)
              header.classList.toggle('shadow-md', isScrolled)
            }
          }}
        >
          <div className="flex flex-col space-y-4">
            <div className="grid grid-cols-1 gap-2">
              {FRONTDESK_LANGUAGE_CATALOG.map((lang) => {
                const FlagIcon = flagForLanguage(lang.code)
                return (
                  <DialogClose
                    key={lang.code}
                    render={
                      <button
                        data-testid={`language-option-${lang.code}`}
                        type="button"
                        aria-label={`${t('self-service.language-selector.title')}: ${lang.label}`}
                        className={cn(
                          'flex items-center justify-between rounded-md border p-4 text-left transition-colors',
                          selectedCode === lang.code
                            ? 'border-primary bg-primary/5'
                            : 'border-border hover:bg-muted/50'
                        )}
                        onClick={() => {
                          if (onLanguageChange) {
                            onLanguageChange(lang.code)
                          } else {
                            void i18n.changeLanguage(lang.code)
                          }
                        }}
                      />
                    }
                  >
                    <span className="text-lg flex flex-row gap-3 items-center font-medium">
                      {FlagIcon && <FlagIcon className="size-8 rounded-sm" />}
                      {lang.label}
                    </span>
                    {selectedCode === lang.code ? (
                      <Check className="size-5 text-primary" />
                    ) : (
                      <ChevronRight className="size-5 text-muted-foreground" />
                    )}
                  </DialogClose>
                )
              })}
            </div>

            <Separator />
            <div className="text-muted-foreground text-sm">
              {t('self-service.language-selector.description')}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
